/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import CustomDialog from 'components/modals/common/CustomDialog';
import _ from 'lodash';
import { getProjectManagerList } from 'apis/setup/SetupProject';
import CustomGrid from 'components/grids/CustomGrid';
import { SubTitleLayout, SubTitleGroup } from 'components/layouts/ContentLayout';
import { useMessageBar } from 'hooks/useMessageBar';
import { useTranslation } from 'react-i18next';
import { SetupProject, SetupProjectCondition } from 'models/setup/SetupProject';

/**
 * 셋업담당자 조회 팝업
 */
interface Props {
  open: boolean;
  close: (isDone?: boolean) => void;
  title?: string;
  condition: any;
}

const SetupManagerPopUp = ({ open, close, title, condition }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();

  const initSearchParam = {
    elmPrjId: '',
    wktTpCd: '',
  };

  const [searchParam, setSearchParam] = useState<SetupProjectCondition>(initSearchParam);
  const [searchState, setSearchState] = useState<boolean>(false);

  const gridRef = useRef<any>();

  const [rowData, setRowData] = useState<SetupProject[]>([]);

  useEffect(() => {
    setSearchParam({
      elmPrjId: condition?.elmPrjId || '',
      wktTpCd: condition?.wktTpCd || '',
    });
    setSearchState(true);
  }, [condition?.elmPrjId, condition?.wktTpCd]);

  useEffect(() => {
    if (searchState) {
      getManagerList();
    }
  }, [searchState]);

  const getManagerList = () => {
    getProjectManagerList(searchParam).then((result: SetupProjectCondition[]) => {
      if (result !== null) {
        setRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
        return false;
      }
    });
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        align: 'center',
        minWidth: 40,
        width: 40,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'wrtUserId',
        header: String(t('com.label.사용자 ID', '사용자 ID')),
        minWidth: 100,
        width: 120,
        align: 'center',
      },
      {
        binding: 'wrtUserNm',
        header: String(t('com.label.사용자명', '사용자명')),
        minWidth: 100,
        width: 120,
        align: 'center',
      },
      {
        binding: 'deptNm',
        header: String(t('com.label.부서명', '부서명')),
        minWidth: 100,
        width: '*',
        align: 'left',
      },
    ];
  }, [rowData]);

  const handleClose = () => {
    close();
  };

  return (
    <>
      <CustomDialog
        title={title || t('setup.label.담당자', '담당자')}
        open={open}
        onClose={handleClose}
        size="md"
      >
        <SubTitleLayout>
          <SubTitleGroup>
            <h3>{t('setup.label.담당자', '담당자')}</h3>
            <span className="total">
              {t('com.label.총', '총')}
              <span>{(rowData || []).length.toLocaleString()}</span>
              {t('com.label.건', '건')}
            </span>
          </SubTitleGroup>
        </SubTitleLayout>
        <CustomGrid
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          initialized={onInitialized}
          height={350}
          isSelector={false}
          isFilter={false}
          allowPinning={false}
          isReadOnly={true}
        />
      </CustomDialog>
    </>
  );
};
export default SetupManagerPopUp;
